import React, { useEffect, useState, useMemo } from "react";
import { db } from "./firebase";
import { collection, getDocs } from "firebase/firestore";
import "./Charts.css";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  ResponsiveContainer
} from "recharts";

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="chart-tooltip">
      <div className="chart-tooltip-label">{label}</div>
      {payload.map((entry, index) => (
        <div
          key={index}
          className="chart-tooltip-row"
          style={{ color: entry.color }}
        >
          <span>{entry.name}:</span>
          <strong>{entry.value}</strong>
        </div>
      ))}
    </div>
  );
}

const PINNED_REP_ORDER = ["AA8", "DN", "MR5", "YK"];
const OTHER_REP_LABEL = "Other";

const COMPARE_COLORS = [
  "#00bcd4",
  "#ff9800",
  "#9c27b0",
  "#4caf50",
  "#3f51b5",
  "#ffc107",
  "#e91e63",
  "#8bc34a",
  "#2196f3",
  "#ff5722"
];

const sortRepsForStore = (repNames) => {
  const cleaned = repNames.filter(Boolean);

  return [...cleaned].sort((a, b) => {
    const aIsOther = a.trim().toLowerCase() === OTHER_REP_LABEL.toLowerCase();
    const bIsOther = b.trim().toLowerCase() === OTHER_REP_LABEL.toLowerCase();

    if (aIsOther && !bIsOther) return 1;
    if (!aIsOther && bIsOther) return -1;

    const aPinnedIndex = PINNED_REP_ORDER.indexOf(a);
    const bPinnedIndex = PINNED_REP_ORDER.indexOf(b);

    const aIsPinned = aPinnedIndex !== -1;
    const bIsPinned = bPinnedIndex !== -1;

    if (aIsPinned && bIsPinned) return aPinnedIndex - bPinnedIndex;
    if (aIsPinned && !bIsPinned) return -1;
    if (!aIsPinned && bIsPinned) return 1;

    return a.localeCompare(b);
  });
};

export default function Charts({ selectedProfile, lockedRepCode, isStoreMode }) {
  const [reports, setReports] = useState([]);
  const [reps, setReps] = useState([]);

  const [tab, setTab] = useState("ATTACH");

  const [filterType, setFilterType] = useState("QTD");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const getDefaultRepFilter = () =>
    isStoreMode ? "ALL" : lockedRepCode || "ALL";

  const [attachCharts, setAttachCharts] = useState([
    {
      id: 1,
      chartType: "LINE",
      attachType: "ALL",
      repFilter: getDefaultRepFilter()
    }
  ]);

  const [salesCharts, setSalesCharts] = useState([
    {
      id: 1,
      chartType: "LINE",
      repFilter: getDefaultRepFilter()
    }
  ]);

  const [compareCharts, setCompareCharts] = useState([
    {
      id: 1,
      mode: "ATTACH",
      attachType: "ALL",
      chartType: "LINE",
      selectedReps: []
    }
  ]);

  useEffect(() => {
    const fetchReports = async () => {
      const snapshot = await getDocs(collection(db, "eodReports"));
      setReports(snapshot.docs.map((doc) => doc.data()));

      const repsSnapshot = await getDocs(collection(db, "reps"));
      const repsData = repsSnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .map((rep) => rep.name)
        .filter(Boolean);

      setReps(sortRepsForStore(repsData));
    };
    fetchReports();
  }, []);

  useEffect(() => {
    const nextRep = isStoreMode ? "ALL" : lockedRepCode || "ALL";

    setAttachCharts((prev) =>
      prev.map((chart) => ({
        ...chart,
        repFilter: isStoreMode ? chart.repFilter || "ALL" : nextRep
      }))
    );

    setSalesCharts((prev) =>
      prev.map((chart) => ({
        ...chart,
        repFilter: isStoreMode ? chart.repFilter || "ALL" : nextRep
      }))
    );
  }, [isStoreMode, lockedRepCode, selectedProfile]);

  const filteredReports = useMemo(() => {
    const today = new Date();
    const todayMidnight = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    return reports.filter((r) => {
      if (!r.date) return false;

      const [y, m, d] = r.date.split("-").map(Number);
      const reportDate = new Date(y, m - 1, d);

      if (filterType === "ALL") return true;

      if (filterType === "MTD") {
        const monthStart = new Date(
          todayMidnight.getFullYear(),
          todayMidnight.getMonth(),
          1
        );
        return reportDate >= monthStart && reportDate <= todayMidnight;
      }

      if (filterType === "YTD") {
        const yearStart = new Date(todayMidnight.getFullYear(), 0, 1);
        return reportDate >= yearStart && reportDate <= todayMidnight;
      }

      if (filterType === "QTD") {
        const month = todayMidnight.getMonth();
        const quarterStartMonth = Math.floor(month / 3) * 3;
        const quarterStart = new Date(
          todayMidnight.getFullYear(),
          quarterStartMonth,
          1
        );
        return reportDate >= quarterStart && reportDate <= todayMidnight;
      }

      if (filterType === "RANGE") {
        if (!startDate || !endDate) return false;

        const [sy, sm, sd] = startDate.split("-").map(Number);
        const [ey, em, ed] = endDate.split("-").map(Number);
        const start = new Date(sy, sm - 1, sd);
        const end = new Date(ey, em - 1, ed);

        return reportDate >= start && reportDate <= end;
      }

      return true;
    });
  }, [reports, filterType, startDate, endDate]);

  const buildGroupedDataForRep = (repFilterValue) => {
    const map = {};
    const effectiveRepFilter = isStoreMode ? repFilterValue : lockedRepCode || "ALL";

    filteredReports.forEach((r) => {
      if (effectiveRepFilter !== "ALL" && r.rep !== effectiveRepFilter) return;

      const date = r.date;
      if (!date) return;

      if (!map[date]) {
        map[date] = {
          date,
          dpSold: 0,
          dpOpp: 0,
          mcEligible: 0,
          mcTotal: 0,
          mcApproved: 0,
          aalEligible: 0,
          aalHits: 0,
          sales: 0
        };
      }

      r.customers?.forEach((c) => {
        const products = c.products || [];

        const isSMB = products.some((p) => p.name.includes("SMB"));

        const hasHUP = products.some(
          (p) => p.name.includes("HUP") && p.qty > 0
        );
        const hasTERM = products.some(
          (p) => p.name.includes("TERM") && p.qty > 0
        );
        const hasEXPU_H = products.some(
          (p) => p.name === "EXPU_H" && p.qty > 0
        );
        const hasEXPU_N = products.some(
          (p) => p.name === "EXPU_N" && p.qty > 0
        );
        const hasEXPU = hasEXPU_H || hasEXPU_N;

        const hasMCAny = products.some(
          (p) =>
            (p.name === "MC APPROVED" || p.name === "MC UNDERREVIEW") &&
            p.qty > 0
        );

        const hasMCApproved = products.some(
          (p) => p.name === "MC APPROVED" && p.qty > 0
        );

        products.forEach((p) => {
          const isDPRelevant =
            p.name.includes("HUP") ||
            p.name.includes("TERM") ||
            p.name === "EXPU_H" ||
            p.name === "EXPU_N";

          if (isDPRelevant && !isSMB) {
            const dpEntries = p.dp || [];

            if (dpEntries.length > 0 && typeof dpEntries[0] === "boolean") {
              map[date].dpOpp += p.qty;
              dpEntries.forEach((entry) => {
                if (entry) map[date].dpSold++;
              });
            } else if (
              dpEntries.length > 0 &&
              typeof dpEntries[0] === "object"
            ) {
              dpEntries.forEach((entry) => {
                if (!entry) return;
                if (entry.value === "HAS") return;

                map[date].dpOpp++;

                if (entry.value === "YES") map[date].dpSold++;
              });
            } else {
              map[date].dpOpp += p.qty;
            }
          }

          if (!["DP", "ACC"].some((x) => p.name.includes(x))) {
            map[date].sales += p.qty;
          }
        });

        const standaloneDP = products.find(
          (p) => p.name === "DP" && p.qty > 0
        );
        if (standaloneDP) {
          map[date].dpSold += standaloneDP.qty;
        }

        const excludedForMC = [
          "SMB",
          "DP",
          "ACC",
          "CHATR",
          "OUTRIGHT SALE",
          "COMWAVE"
        ];

        const hasValidProduct = products.some(
          (p) =>
            !excludedForMC.some((ex) => p.name.includes(ex)) &&
            p.name !== "MC APPROVED" &&
            p.name !== "MC UNDERREVIEW" &&
            p.qty > 0
        );

        const hasOnlyMC =
          hasMCAny &&
          !products.some(
            (p) =>
              p.name !== "MC APPROVED" &&
              p.name !== "MC UNDERREVIEW" &&
              p.qty > 0
          );

        if (!c.hadMCBefore) {
          if (hasValidProduct) {
            map[date].mcEligible++;
            if (hasMCAny) map[date].mcTotal++;
            if (hasMCApproved) map[date].mcApproved++;
          } else if (hasOnlyMC) {
            map[date].mcEligible++;
            map[date].mcTotal++;
            if (hasMCApproved) map[date].mcApproved++;
          }
        }

        if (!isSMB && (hasHUP || hasTERM || hasEXPU)) {
          map[date].aalEligible++;
          map[date].aalHits += c.aalCount || 0;
        }
      });
    });

    const rows = Object.values(map).sort((a, b) =>
      a.date.localeCompare(b.date)
    );

    return rows.map((d) => {
      const dp = d.dpOpp ? (d.dpSold / d.dpOpp) * 100 : 0;
      const mc = d.mcEligible ? (d.mcTotal / d.mcEligible) * 100 : 0;
      const aal = d.aalEligible ? (d.aalHits / d.aalEligible) * 100 : 0;

      const totalOpp = d.dpOpp + d.mcEligible + d.aalEligible;
      const totalSuccess = d.dpSold + d.mcTotal + d.aalHits;
      const ALL = totalOpp ? (totalSuccess / totalOpp) * 100 : 0;

      return {
        date: d.date,
        DP: Number(dp.toFixed(1)),
        MC: Number(mc.toFixed(1)),
        AAL: Number(aal.toFixed(1)),
        ALL: Number(ALL.toFixed(1)),
        Sales: d.sales
      };
    });
  };

  const compareData = useMemo(() => {
    if (!isStoreMode) return [];

    const map = {};

    filteredReports.forEach((r) => {
      const date = r.date;
      if (!date || !r.rep) return;

      if (!map[date]) map[date] = {};
      if (!map[date][r.rep]) {
        map[date][r.rep] = {
          dpSold: 0,
          dpOpp: 0,
          mcEligible: 0,
          mcTotal: 0,
          mcApproved: 0,
          aalEligible: 0,
          aalHits: 0,
          sales: 0
        };
      }

      const bucket = map[date][r.rep];

      r.customers?.forEach((c) => {
        const products = c.products || [];

        const isSMB = products.some((p) => p.name.includes("SMB"));

        const hasHUP = products.some(
          (p) => p.name.includes("HUP") && p.qty > 0
        );
        const hasTERM = products.some(
          (p) => p.name.includes("TERM") && p.qty > 0
        );
        const hasEXPU_H = products.some(
          (p) => p.name === "EXPU_H" && p.qty > 0
        );
        const hasEXPU_N = products.some(
          (p) => p.name === "EXPU_N" && p.qty > 0
        );
        const hasEXPU = hasEXPU_H || hasEXPU_N;

        const hasMCAny = products.some(
          (p) =>
            (p.name === "MC APPROVED" || p.name === "MC UNDERREVIEW") &&
            p.qty > 0
        );

        const hasMCApproved = products.some(
          (p) => p.name === "MC APPROVED" && p.qty > 0
        );

        products.forEach((p) => {
          const isDPRelevant =
            p.name.includes("HUP") ||
            p.name.includes("TERM") ||
            p.name === "EXPU_H" ||
            p.name === "EXPU_N";

          if (isDPRelevant && !isSMB) {
            const dpEntries = p.dp || [];

            if (dpEntries.length > 0 && typeof dpEntries[0] === "boolean") {
              bucket.dpOpp += p.qty;
              dpEntries.forEach((entry) => {
                if (entry) bucket.dpSold++;
              });
            } else if (
              dpEntries.length > 0 &&
              typeof dpEntries[0] === "object"
            ) {
              dpEntries.forEach((entry) => {
                if (!entry) return;
                if (entry.value === "HAS") return;

                bucket.dpOpp++;

                if (entry.value === "YES") bucket.dpSold++;
              });
            } else {
              bucket.dpOpp += p.qty;
            }
          }

          if (!["DP", "ACC"].some((x) => p.name.includes(x))) {
            bucket.sales += p.qty;
          }
        });

        const standaloneDP = products.find(
          (p) => p.name === "DP" && p.qty > 0
        );
        if (standaloneDP) {
          bucket.dpSold += standaloneDP.qty;
        }

        const excludedForMC = [
          "SMB",
          "DP",
          "ACC",
          "CHATR",
          "OUTRIGHT SALE",
          "COMWAVE"
        ];

        const hasValidProduct = products.some(
          (p) =>
            !excludedForMC.some((ex) => p.name.includes(ex)) &&
            p.name !== "MC APPROVED" &&
            p.name !== "MC UNDERREVIEW" &&
            p.qty > 0
        );

        const hasOnlyMC =
          hasMCAny &&
          !products.some(
            (p) =>
              p.name !== "MC APPROVED" &&
              p.name !== "MC UNDERREVIEW" &&
              p.qty > 0
          );

        if (!c.hadMCBefore) {
          if (hasValidProduct) {
            bucket.mcEligible++;
            if (hasMCAny) bucket.mcTotal++;
            if (hasMCApproved) bucket.mcApproved++;
          } else if (hasOnlyMC) {
            bucket.mcEligible++;
            bucket.mcTotal++;
            if (hasMCApproved) bucket.mcApproved++;
          }
        }

        if (!isSMB && (hasHUP || hasTERM || hasEXPU)) {
          bucket.aalEligible++;
          bucket.aalHits += c.aalCount || 0;
        }
      });
    });

    const dates = Object.keys(map).sort();
    return dates.map((date) => {
      const entry = { date };
      Object.keys(map[date]).forEach((repCode) => {
        const d = map[date][repCode];

        const dp = d.dpOpp ? (d.dpSold / d.dpOpp) * 100 : 0;
        const mc = d.mcEligible ? (d.mcTotal / d.mcEligible) * 100 : 0;
        const aal = d.aalEligible ? (d.aalHits / d.aalEligible) * 100 : 0;

        const totalOpp = d.dpOpp + d.mcEligible + d.aalEligible;
        const totalSuccess = d.dpSold + d.mcTotal + d.aalHits;
        const ALL = totalOpp ? (totalSuccess / totalOpp) * 100 : 0;

        entry[repCode] = {
          DP: Number(dp.toFixed(1)),
          MC: Number(mc.toFixed(1)),
          AAL: Number(aal.toFixed(1)),
          ALL: Number(ALL.toFixed(1)),
          Sales: d.sales
        };
      });
      return entry;
    });
  }, [filteredReports, isStoreMode]);

  const getRepColor = (repCode) => {
    const idx = reps.indexOf(repCode);
    if (idx === -1) return "#cccccc";
    return COMPARE_COLORS[idx % COMPARE_COLORS.length];
  };

  const addAttachChart = () => {
    setAttachCharts((prev) => [
      ...prev,
      {
        id: prev.length ? prev[prev.length - 1].id + 1 : 1,
        chartType: "LINE",
        attachType: "ALL",
        repFilter: getDefaultRepFilter()
      }
    ]);
  };

  const updateAttachChart = (id, patch) => {
    setAttachCharts((prev) =>
      prev.map((chart) => (chart.id === id ? { ...chart, ...patch } : chart))
    );
  };

  const addSalesChart = () => {
    setSalesCharts((prev) => [
      ...prev,
      {
        id: prev.length ? prev[prev.length - 1].id + 1 : 1,
        chartType: "LINE",
        repFilter: getDefaultRepFilter()
      }
    ]);
  };

  const updateSalesChart = (id, patch) => {
    setSalesCharts((prev) =>
      prev.map((chart) => (chart.id === id ? { ...chart, ...patch } : chart))
    );
  };

  const addCompareChart = () => {
    setCompareCharts((prev) => [
      ...prev,
      {
        id: prev.length ? prev[prev.length - 1].id + 1 : 1,
        mode: "ATTACH",
        attachType: "ALL",
        chartType: "LINE",
        selectedReps: []
      }
    ]);
  };

  const updateCompareChart = (id, patch) => {
    setCompareCharts((prev) =>
      prev.map((c) => (c.id === id ? { ...c, ...patch } : c))
    );
  };

  const toggleCompareRep = (chartId, repCode) => {
    setCompareCharts((prev) =>
      prev.map((c) => {
        if (c.id !== chartId) return c;
        const exists = c.selectedReps.includes(repCode);
        return {
          ...c,
          selectedReps: exists
            ? c.selectedReps.filter((r) => r !== repCode)
            : [...c.selectedReps, repCode]
        };
      })
    );
  };

  useEffect(() => {
    if (!isStoreMode || !reps.length) return;

    setCompareCharts((prev) =>
      prev.map((c) =>
        c.selectedReps && c.selectedReps.length
          ? c
          : { ...c, selectedReps: reps.slice(0, 3) }
      )
    );
  }, [reps, isStoreMode]);

  const renderSingleMetricChart = ({
    data,
    chartType,
    dataKey,
    label
  }) => {
    return (
      <div className="charts-card">
        <div className="charts-card-header">
          <span className="charts-card-label">{label}</span>
          <span className="charts-card-meta">{chartType} Chart</span>
        </div>

        <div className="charts-area">
          <ResponsiveContainer width="100%" height="100%">
            {chartType === "LINE" ? (
              <LineChart
                data={data}
                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.08)"
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  tick={{ fill: "#b1b1b1", fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fill: "#b1b1b1", fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey={dataKey}
                  stroke="#ff3131"
                  strokeWidth={3}
                  dot={{ r: 3, fill: "#ff3131", strokeWidth: 0 }}
                  activeDot={{
                    r: 6,
                    fill: "#ffffff",
                    stroke: "#ff3131",
                    strokeWidth: 3
                  }}
                />
              </LineChart>
            ) : (
              <BarChart
                data={data}
                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.08)"
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  tick={{ fill: "#b1b1b1", fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fill: "#b1b1b1", fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey={dataKey}
                  fill="#ff3131"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderCompareChart = (chart) => {
    const { id, mode, attachType, chartType, selectedReps } = chart;

    const metricKey =
      mode === "ATTACH"
        ? attachType === "ALL"
          ? "ALL"
          : attachType
        : "Sales";

    const baseStoreData = buildGroupedDataForRep("ALL");

    const data = baseStoreData.map((storeRow) => {
      const compareRow = compareData.find((r) => r.date === storeRow.date);
      const out = {
        date: storeRow.date,
        STORE__value: metricKey === "Sales" ? storeRow.Sales : storeRow[metricKey]
      };

      selectedReps.forEach((repCode) => {
        const repEntry = compareRow?.[repCode];
        if (!repEntry) return;
        out[`${repCode}__value`] =
          metricKey === "Sales" ? repEntry.Sales : repEntry[metricKey];
      });

      return out;
    });

    return (
      <div key={id} className="charts-card compare-card">
        <div className="charts-card-header">
          <span className="charts-card-label">
            Compare – {mode === "ATTACH" ? `${attachType} Attach` : "Sales"}
          </span>
          <span className="charts-card-meta">{chartType} Chart</span>
        </div>

        <div className="charts-controls charts-controls-compare">
          <select
            className="charts-select"
            value={chartType}
            onChange={(e) =>
              updateCompareChart(id, { chartType: e.target.value })
            }
          >
            <option value="LINE">Line</option>
            <option value="BAR">Bar</option>
          </select>

          <select
            className="charts-select"
            value={mode}
            onChange={(e) =>
              updateCompareChart(id, { mode: e.target.value })
            }
          >
            <option value="ATTACH">Attach</option>
            <option value="SALES">Sales</option>
          </select>

          {mode === "ATTACH" && (
            <select
              className="charts-select"
              value={attachType}
              onChange={(e) =>
                updateCompareChart(id, { attachType: e.target.value })
              }
            >
              <option value="ALL">ALL</option>
              <option value="DP">DP</option>
              <option value="MC">MC</option>
              <option value="AAL">AAL</option>
            </select>
          )}
        </div>

        <div className="charts-compare-reps">
          <div className="charts-compare-reps-label">Reps:</div>
          <div className="charts-compare-reps-list">
            <button
              type="button"
              className="charts-rep-pill active"
              style={{
                borderColor: "#ff3131",
                backgroundColor: "rgba(0,0,0,0.25)"
              }}
            >
              <span
                className="charts-rep-dot"
                style={{ backgroundColor: "#ff3131" }}
              />
              Store
            </button>

            {reps.map((repCode) => {
              const active = selectedReps.includes(repCode);
              const color = getRepColor(repCode);

              return (
                <button
                  key={repCode}
                  type="button"
                  className={`charts-rep-pill ${active ? "active" : ""}`}
                  style={
                    active
                      ? {
                          borderColor: color,
                          backgroundColor: "rgba(0,0,0,0.25)"
                        }
                      : undefined
                  }
                  onClick={() => toggleCompareRep(id, repCode)}
                >
                  <span
                    className="charts-rep-dot"
                    style={{ backgroundColor: color }}
                  />
                  {repCode}
                </button>
              );
            })}
          </div>
        </div>

        <div className="charts-area">
          <ResponsiveContainer width="100%" height="100%">
            {chartType === "LINE" ? (
              <LineChart
                data={data}
                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.08)"
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  tick={{ fill: "#b1b1b1", fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fill: "#b1b1b1", fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip content={<CustomTooltip />} />

                <Line
                  type="monotone"
                  dataKey="STORE__value"
                  name="Store"
                  stroke="#ff3131"
                  strokeWidth={3}
                  dot={{ r: 3, fill: "#ff3131", strokeWidth: 0 }}
                  activeDot={{
                    r: 6,
                    fill: "#ffffff",
                    stroke: "#ff3131",
                    strokeWidth: 3
                  }}
                />

                {selectedReps.map((repCode) => {
                  const key = `${repCode}__value`;
                  const color = getRepColor(repCode);
                  return (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      name={repCode}
                      stroke={color}
                      strokeWidth={3}
                      dot={{ r: 3, fill: color, strokeWidth: 0 }}
                      activeDot={{
                        r: 6,
                        fill: "#ffffff",
                        stroke: color,
                        strokeWidth: 3
                      }}
                    />
                  );
                })}
              </LineChart>
            ) : (
              <BarChart
                data={data}
                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.08)"
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  tick={{ fill: "#b1b1b1", fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fill: "#b1b1b1", fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip content={<CustomTooltip />} />

                <Bar
                  dataKey="STORE__value"
                  name="Store"
                  fill="#ff3131"
                  radius={[8, 8, 0, 0]}
                />

                {selectedReps.map((repCode) => {
                  const key = `${repCode}__value`;
                  const color = getRepColor(repCode);
                  return (
                    <Bar
                      key={key}
                      dataKey={key}
                      name={repCode}
                      fill={color}
                      radius={[8, 8, 0, 0]}
                    />
                  );
                })}
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  return (
    <div className="charts-page">
      <div className="charts-shell">
        <h2 className="charts-title">Charts</h2>

        <div className="charts-subtabs">
          <button
            className={`charts-tab-btn ${tab === "ATTACH" ? "active" : ""}`}
            onClick={() => setTab("ATTACH")}
          >
            Attach
          </button>

          <button
            className={`charts-tab-btn ${tab === "SALES" ? "active" : ""}`}
            onClick={() => setTab("SALES")}
          >
            Sales
          </button>

          {isStoreMode && (
            <button
              className={`charts-tab-btn ${tab === "COMPARE" ? "active" : ""}`}
              onClick={() => setTab("COMPARE")}
            >
              Compare
            </button>
          )}
        </div>

        <div className="charts-controls">
          <select
            className="charts-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="QTD">Quarter To Date</option>
            <option value="MTD">Month To Date</option>
            <option value="YTD">Year To Date</option>
            <option value="RANGE">Custom Range</option>
            <option value="ALL">All Time</option>
          </select>

          {filterType === "RANGE" && (
            <div className="charts-range">
              <input
                className="charts-input"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <input
                className="charts-input"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          )}
        </div>

        {tab === "ATTACH" && (
          <>
            <div className="charts-compare-header">
              <div className="charts-compare-title">Attach charts</div>
              <button
                type="button"
                className="charts-add-compare-btn"
                onClick={addAttachChart}
              >
                + Add Chart
              </button>
            </div>

            {attachCharts.map((chart) => {
              const data = buildGroupedDataForRep(chart.repFilter);
              const dataKey =
                chart.attachType === "ALL" ? "ALL" : chart.attachType;

              return (
                <div key={chart.id}>
                  <div className="charts-controls charts-controls-compare">
                    <select
                      className="charts-select"
                      value={chart.chartType}
                      onChange={(e) =>
                        updateAttachChart(chart.id, { chartType: e.target.value })
                      }
                    >
                      <option value="LINE">Line</option>
                      <option value="BAR">Bar</option>
                    </select>

                    {isStoreMode ? (
                      <select
                        className="charts-select"
                        value={chart.repFilter}
                        onChange={(e) =>
                          updateAttachChart(chart.id, { repFilter: e.target.value })
                        }
                      >
                        <option value="ALL">All Reps</option>
                        {reps.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="charts-select charts-select-locked">
                        Rep: {lockedRepCode}
                      </div>
                    )}

                    <select
                      className="charts-select"
                      value={chart.attachType}
                      onChange={(e) =>
                        updateAttachChart(chart.id, { attachType: e.target.value })
                      }
                    >
                      <option value="ALL">ALL</option>
                      <option value="DP">DP</option>
                      <option value="MC">MC</option>
                      <option value="AAL">AAL</option>
                    </select>
                  </div>

                  {renderSingleMetricChart({
                    data,
                    chartType: chart.chartType,
                    dataKey,
                    label: `${chart.attachType} Trend`
                  })}
                </div>
              );
            })}
          </>
        )}

        {tab === "SALES" && (
          <>
            <div className="charts-compare-header">
              <div className="charts-compare-title">Sales charts</div>
              <button
                type="button"
                className="charts-add-compare-btn"
                onClick={addSalesChart}
              >
                + Add Chart
              </button>
            </div>

            {salesCharts.map((chart) => {
              const data = buildGroupedDataForRep(chart.repFilter);

              return (
                <div key={chart.id}>
                  <div className="charts-controls charts-controls-compare">
                    <select
                      className="charts-select"
                      value={chart.chartType}
                      onChange={(e) =>
                        updateSalesChart(chart.id, { chartType: e.target.value })
                      }
                    >
                      <option value="LINE">Line</option>
                      <option value="BAR">Bar</option>
                    </select>

                    {isStoreMode ? (
                      <select
                        className="charts-select"
                        value={chart.repFilter}
                        onChange={(e) =>
                          updateSalesChart(chart.id, { repFilter: e.target.value })
                        }
                      >
                        <option value="ALL">All Reps</option>
                        {reps.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="charts-select charts-select-locked">
                        Rep: {lockedRepCode}
                      </div>
                    )}
                  </div>

                  {renderSingleMetricChart({
                    data,
                    chartType: chart.chartType,
                    dataKey: "Sales",
                    label: "Sales Trend"
                  })}
                </div>
              );
            })}
          </>
        )}

        {tab === "COMPARE" && isStoreMode && (
          <>
            <div className="charts-compare-header">
              <div className="charts-compare-title">Compare multiple reps</div>
              <button
                type="button"
                className="charts-add-compare-btn"
                onClick={addCompareChart}
              >
                + Add Compare Chart
              </button>
            </div>

            {compareCharts.map((chart) => renderCompareChart(chart))}
          </>
        )}
      </div>
    </div>
  );
}